import styled, { keyframes } from "styled-components";

/* ═══════════════════════════════════════════════════════════════════════
   AddToFolderModal — FOLDER GRID COMPOSITION
   New structure: centered hero header, prominent "Create new folder"
   bar at the top, folder list as selection cards (each card has folder
   icon + name + check indicator). Favorites card styled distinctly with
   a heart/rose tint. Primary "Add" CTA at the bottom.
   ═══════════════════════════════════════════════════════════════════════ */

const panelFloat = keyframes`
  0%, 100% { filter: brightness(1); }
  50%      { filter: brightness(1.02); }
`;

const buttonShimmer = keyframes`
  0%   { left: -100%; }
  100% { left: 150%; }
`;

// ─── Shell ─────────────────────────────────────────────────────────────

export const ModalContainer = styled.div`
  position: relative;
  width: 100%;
  max-width: min(92dvw, 460px);
  max-height: 90dvh;
  overflow: hidden;
  border-radius: 28px;
  border: 1px solid var(--glass-edge);
  background:
    radial-gradient(
      120% 60% at 30% -10%,
      rgba(var(--glass-white-rgb), 0.96) 0%,
      rgba(var(--glass-pale-rgb), 0.9) 55%,
      rgba(var(--glass-pale-rgb), 0.82) 100%
    ),
    linear-gradient(
      180deg,
      rgba(var(--glass-white-rgb), 0.94) 0%,
      rgba(var(--glass-pale-rgb), 0.9) 100%
    );
  backdrop-filter: blur(28px) saturate(180%);
  -webkit-backdrop-filter: blur(28px) saturate(180%);
  box-shadow:
    0 32px 64px -18px rgba(var(--color-brand-500-rgb), 0.4),
    0 12px 28px rgba(var(--color-brand-400-rgb), 0.22),
    inset 0 2px 0 rgba(255, 255, 255, 0.95),
    inset 0 -10px 22px rgba(var(--color-brand-200-rgb), 0.26),
    inset 0 0 0 1px rgba(255, 255, 255, 0.45);
  animation: ${panelFloat} 5.5s ease-in-out infinite;

  &::before {
    content: "";
    position: absolute;
    top: 2px;
    left: 32px;
    right: 32px;
    height: 18px;
    border-radius: 999px;
    background: linear-gradient(
      180deg,
      rgba(255, 255, 255, 0.9) 0%,
      rgba(255, 255, 255, 0) 100%
    );
    filter: blur(2px);
    pointer-events: none;
    z-index: 10;
  }

  [data-theme="dark"] &::before {
    opacity: 0;
  }

  @media (max-width: 475px) {
    border-radius: 24px;
  }

  [data-theme="dark"] & {
    border-color: transparent;
    box-shadow:
      0 28px 56px -18px rgba(0, 0, 0, 0.6),
      0 10px 24px rgba(0, 0, 0, 0.4),
      inset 0 -10px 22px rgba(var(--color-brand-700-rgb), 0.18);
  }
`;

export const ModalContent = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  /* No horizontal padding on the shell — each child owns its own gutter
     so nested hover transforms/shadows never clip against a modal edge. */
  padding: 24px 0 22px;
  gap: 16px;
  max-height: 82dvh;

  @media (max-width: 475px) {
    padding: 20px 0 18px;
    gap: 14px;
  }
`;

// ─── Header row (Title + Close) ────────────────────────────────────────

export const Row = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 0 22px;

  @media (max-width: 475px) {
    padding: 0 18px;
  }
`;

export const ModalTitle = styled.h2`
  margin: 0;
  font-family: var(--font-family-primary);
  font-size: 22px;
  font-weight: 800;
  letter-spacing: -0.02em;
  line-height: normal;

  /* Light-water gradient that flips cleanly for dark theme via the
     text-strong/text-body tokens. */
  background: linear-gradient(
    0deg,
    var(--color-text-strong) 0%,
    var(--color-text-body) 45%,
    var(--color-brand-600) 100%
  );
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  color: transparent;
  filter: drop-shadow(0 1px 2px rgba(var(--color-brand-600-rgb), 0.14));
`;

export const CloseButton = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  flex-shrink: 0;
  border-radius: 50%;
  border: 1px solid rgba(var(--glass-white-rgb), 0.7);
  background: radial-gradient(
    130% 100% at 30% 15%,
    rgba(var(--glass-white-rgb), 0.85) 0%,
    rgba(var(--glass-white-rgb), 0.58) 45%,
    rgba(var(--glass-pale-rgb), 0.42) 100%
  );
  backdrop-filter: blur(16px) saturate(180%);
  -webkit-backdrop-filter: blur(16px) saturate(180%);
  box-shadow:
    0 6px 14px rgba(var(--color-brand-500-rgb), 0.2),
    inset 0 1px 0 rgba(255, 255, 255, 0.95);
  cursor: pointer;
  outline: none;
  overflow: hidden;
  color: var(--color-brand-700);
  transition:
    transform 200ms var(--ease-snappy),
    border-color 200ms var(--ease-snappy);

  &::before {
    content: "";
    position: absolute;
    top: 3px;
    left: 5px;
    width: 14px;
    height: 8px;
    border-radius: 50%;
    background: linear-gradient(
      180deg,
      rgba(255, 255, 255, 0.88) 0%,
      rgba(255, 255, 255, 0) 100%
    );
    filter: blur(1px);
    pointer-events: none;
  }

  [data-theme="dark"] &::before {
    opacity: 0;
  }

  svg {
    position: relative;
    z-index: 1;
    path {
      stroke: var(--color-brand-700);
    }
  }

  &:hover {
    transform: translateY(-1px) scale(1.05);
    border-color: rgba(var(--color-brand-400-rgb), 0.55);
  }

  [data-theme="dark"] & {
    border-color: transparent;
    background: var(--color-surface-3);
    box-shadow:
      0 4px 10px rgba(0, 0, 0, 0.45),
      inset 0 -2px 6px rgba(var(--color-brand-700-rgb), 0.16);
    color: var(--color-brand-300);

    svg path {
      stroke: var(--color-brand-300);
    }
  }
`;

// ─── Input container (hosts folder list + create-new at bottom) ────────

export const InputContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  min-height: 0;
  /* Horizontal gutter lives on this container now, not on the modal shell.
     This gives hover lifts room inside the scroll area without clipping. */
  padding: 0 22px;

  @media (max-width: 475px) {
    padding: 0 18px;
  }
`;

export const Label = styled.p`
  margin: 0 0 4px;
  color: var(--color-brand-700);
  opacity: 0.62;
  font-family: var(--font-family-primary);
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.1em;
  text-transform: uppercase;

  [data-theme="dark"] & {
    color: var(--color-brand-300);
    opacity: 0.7;
  }
`;

// ─── Folder list — scroll container ────────────────────────────────────

export const FolderListContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  overflow-y: auto;
  max-height: 300px;
  /* Breathing room so hover translateY/scale and card shadows don't clip
     against the container edges (top/bottom/left/right). */
  padding: 6px 8px 6px 4px;
  margin: -6px -4px -6px -4px;

  scrollbar-width: thin;
  scrollbar-color: rgba(var(--color-brand-300-rgb), 0.38) transparent;

  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(var(--color-brand-300-rgb), 0.38);
    border-radius: 999px;
  }
  &::-webkit-scrollbar-thumb:hover {
    background: rgba(var(--color-brand-300-rgb), 0.55);
  }
`;

// ─── Folder item — selection card with water glass styling ───────────

export const FolderItem = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 12px 14px;
  min-height: 48px;
  border-radius: 14px;
  border: 1px solid
    ${({ $isSelected }) =>
      $isSelected
        ? "rgba(var(--color-brand-400-rgb), 0.55)"
        : "rgba(var(--color-brand-300-rgb), 0.22)"};
  background: ${({ $isSelected }) =>
    $isSelected
      ? `radial-gradient(
          130% 100% at 30% 15%,
          rgba(var(--glass-pale-rgb), 0.95) 0%,
          rgba(var(--glass-pale-rgb), 0.82) 55%,
          rgba(var(--color-brand-200-rgb), 0.72) 100%
        )`
      : `radial-gradient(
          130% 100% at 30% 15%,
          rgba(var(--glass-white-rgb), 0.82) 0%,
          rgba(var(--glass-white-rgb), 0.54) 45%,
          rgba(var(--glass-pale-rgb), 0.42) 100%
        )`};
  backdrop-filter: blur(18px) saturate(180%);
  -webkit-backdrop-filter: blur(18px) saturate(180%);
  box-shadow: ${({ $isSelected }) =>
    $isSelected
      ? `0 6px 16px rgba(var(--color-brand-500-rgb), 0.24),
         inset 0 1px 0 rgba(255, 255, 255, 0.9),
         inset 0 -2px 8px rgba(var(--color-brand-200-rgb), 0.22)`
      : `0 3px 8px rgba(var(--color-brand-500-rgb), 0.1),
         inset 0 1px 0 rgba(255, 255, 255, 0.88)`};
  cursor: pointer;
  overflow: hidden;
  transition:
    transform 180ms var(--ease-snappy),
    border-color 180ms var(--ease-snappy),
    background 180ms var(--ease-snappy),
    box-shadow 180ms var(--ease-snappy);

  /* Top sheen */
  &::before {
    content: "";
    position: absolute;
    top: 2px;
    left: 18px;
    right: 18px;
    height: 8px;
    border-radius: 999px;
    background: linear-gradient(
      180deg,
      rgba(255, 255, 255, 0.72) 0%,
      rgba(255, 255, 255, 0) 100%
    );
    filter: blur(1.2px);
    pointer-events: none;
  }

  [data-theme="dark"] &::before {
    opacity: 0;
  }

  &:hover {
    transform: translateY(-1px);
    border-color: ${({ $isSelected }) =>
      $isSelected
        ? "rgba(var(--color-brand-400-rgb), 0.72)"
        : "rgba(var(--color-brand-400-rgb), 0.45)"};
    box-shadow: ${({ $isSelected }) =>
      $isSelected
        ? `0 10px 22px rgba(var(--color-brand-500-rgb), 0.3),
           inset 0 1px 0 rgba(255, 255, 255, 0.95)`
        : `0 6px 14px rgba(var(--color-brand-500-rgb), 0.18),
           inset 0 1px 0 rgba(255, 255, 255, 0.95)`};
  }

  svg {
    flex-shrink: 0;
    color: ${({ $isSelected }) =>
      $isSelected ? "var(--color-brand-600)" : "var(--color-brand-700)"};
    path {
      stroke: ${({ $isSelected }) =>
        $isSelected ? "var(--color-brand-600)" : "var(--color-brand-700)"};
    }
  }

  [data-theme="dark"] & {
    border-color: ${({ $isSelected }) =>
      $isSelected ? "rgba(var(--color-brand-400-rgb), 0.45)" : "transparent"};
    background: ${({ $isSelected }) =>
      $isSelected
        ? "rgba(var(--color-brand-700-rgb), 0.32)"
        : "rgba(var(--color-brand-900-rgb), 0.4)"};
    box-shadow: ${({ $isSelected }) =>
      $isSelected
        ? "0 4px 12px rgba(0, 0, 0, 0.45)"
        : "0 2px 6px rgba(0, 0, 0, 0.32)"};
    color: #ffffff;

    svg {
      color: #ffffff;
      path {
        stroke: #ffffff;
      }
    }

    &:hover {
      background: ${({ $isSelected }) =>
        $isSelected
          ? "rgba(var(--color-brand-700-rgb), 0.42)"
          : "rgba(var(--color-brand-900-rgb), 0.55)"};
      border-color: rgba(var(--color-brand-400-rgb), 0.4);
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.5);
    }
  }
`;

export const FolderName = styled.span`
  flex: 1;
  min-width: 0;
  color: var(--color-brand-700);
  font-family: var(--font-family-primary);
  font-size: 14px;
  font-weight: 700;
  letter-spacing: -0.005em;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  [data-theme="dark"] & {
    color: #ffffff;
  }
`;

// ─── Button row ────────────────────────────────────────────────────────

export const ButtonGroup = styled.div`
  display: flex;
  gap: 10px;
  width: 100%;
  padding: 12px 22px 0;
  border-top: 1px solid rgba(var(--color-brand-300-rgb), 0.18);
  margin-top: 4px;

  @media (max-width: 475px) {
    padding: 12px 18px 0;
  }
`;

/* Cancel — frosted light water pill (secondary) */
export const CancelButton = styled.button`
  position: relative;
  display: flex;
  flex: 0 0 auto;
  min-width: 104px;
  min-height: 46px;
  padding: 0 20px;
  justify-content: center;
  align-items: center;
  gap: 8px;
  border-radius: 999px;
  border: 1px solid rgba(var(--color-brand-300-rgb), 0.26);
  background: radial-gradient(
    120% 100% at 20% 0%,
    rgba(var(--glass-white-rgb), 0.88) 0%,
    rgba(var(--glass-pale-rgb), 0.62) 55%,
    rgba(var(--glass-pale-rgb), 0.48) 100%
  );
  backdrop-filter: blur(16px) saturate(180%);
  -webkit-backdrop-filter: blur(16px) saturate(180%);
  box-shadow:
    0 6px 14px rgba(var(--color-brand-500-rgb), 0.12),
    inset 0 1px 0 rgba(255, 255, 255, 0.92);
  color: var(--color-brand-700);
  font-family: var(--font-family-primary);
  font-size: 14px;
  font-weight: 700;
  letter-spacing: -0.005em;
  cursor: pointer;
  outline: none;
  overflow: hidden;
  transition:
    transform 200ms var(--ease-snappy),
    border-color 200ms var(--ease-snappy);

  &::before {
    content: "";
    position: absolute;
    top: 2px;
    left: 14px;
    right: 14px;
    height: 8px;
    border-radius: 999px;
    background: linear-gradient(
      180deg,
      rgba(255, 255, 255, 0.78) 0%,
      rgba(255, 255, 255, 0) 100%
    );
    filter: blur(1.2px);
    pointer-events: none;
  }

  [data-theme="dark"] &::before {
    opacity: 0;
  }

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    border-color: rgba(var(--color-brand-400-rgb), 0.55);
  }

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }

  [data-theme="dark"] & {
    border-color: transparent;
    background: var(--color-surface-3);
    box-shadow:
      0 4px 10px rgba(0, 0, 0, 0.45),
      inset 0 -2px 6px rgba(var(--color-brand-700-rgb), 0.16);
    color: var(--color-brand-300);

    &:hover:not(:disabled) {
      background: rgba(var(--color-brand-700-rgb), 0.32);
    }
  }
`;

/* Confirm — brand gradient primary CTA with shimmer */
export const ConfirmButton = styled.button`
  position: relative;
  display: flex;
  flex: 1;
  min-height: 46px;
  padding: 0 22px;
  justify-content: center;
  align-items: center;
  gap: 8px;
  border-radius: 999px;
  border: 1px solid rgba(var(--glass-white-rgb), 0.72);
  background: linear-gradient(
    180deg,
    var(--color-brand-400) 0%,
    var(--color-brand-600) 55%,
    var(--color-brand-700) 100%
  );
  box-shadow:
    0 14px 28px rgba(var(--color-brand-600-rgb), 0.36),
    inset 0 1px 0 rgba(255, 255, 255, 0.55),
    inset 0 -3px 6px rgba(var(--color-brand-700-rgb), 0.3);
  color: var(--glass-text-on-pill);
  font-family: var(--font-family-primary);
  font-size: 14px;
  font-weight: 800;
  letter-spacing: -0.005em;
  text-shadow: 0 1px 1px rgba(var(--color-brand-900-rgb), 0.28);
  cursor: pointer;
  outline: none;
  overflow: hidden;
  transition:
    transform 200ms var(--ease-snappy),
    filter 200ms var(--ease-snappy),
    box-shadow 200ms var(--ease-snappy),
    opacity 200ms var(--ease-snappy);

  &::before {
    content: "";
    position: absolute;
    top: 2px;
    left: 16px;
    right: 16px;
    height: 9px;
    border-radius: 999px;
    background: linear-gradient(
      180deg,
      rgba(255, 255, 255, 0.72) 0%,
      rgba(255, 255, 255, 0) 100%
    );
    filter: blur(1.5px);
    pointer-events: none;
    z-index: 1;
  }

  [data-theme="dark"] &::before {
    opacity: 0;
  }

  &::after {
    content: "";
    position: absolute;
    top: 0;
    left: -100%;
    width: 45%;
    height: 100%;
    background: linear-gradient(
      90deg,
      rgba(255, 255, 255, 0) 0%,
      rgba(255, 255, 255, 0.38) 50%,
      rgba(255, 255, 255, 0) 100%
    );
    animation: ${buttonShimmer} 4s infinite;
    pointer-events: none;
  }

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    filter: brightness(1.06);
    box-shadow:
      0 18px 34px rgba(var(--color-brand-600-rgb), 0.46),
      inset 0 1px 0 rgba(255, 255, 255, 0.7),
      inset 0 -3px 8px rgba(var(--color-brand-700-rgb), 0.38);
  }

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }

  l-bouncy {
    position: relative;
    z-index: 2;
  }

  [data-theme="dark"] & {
    border-color: transparent;
  }
`;

// ─── Simple text input (kept export for JS compat) ─────────────────────

export const Input = styled.input`
  width: 100%;
  height: 44px;
  padding: 0 14px;
  border-radius: 12px;
  border: 1px solid rgba(var(--color-brand-300-rgb), 0.28);
  background: var(--glass-base-2);
  color: var(--color-brand-700);
  font-family: var(--font-family-primary);
  font-size: 14px;
  font-weight: 600;
  outline: none;
  transition: border-color 200ms var(--ease-snappy);

  &::placeholder {
    color: var(--color-brand-700);
    opacity: 0.48;
  }

  &:focus {
    border-color: rgba(var(--color-brand-400-rgb), 0.6);
    box-shadow: 0 0 0 3px rgba(var(--color-brand-400-rgb), 0.14);
  }
`;

// ─── Specialized folder-item variants (favorite + create-new CTA) ──────

export const FavoriteFolderItem = styled(FolderItem)`
  border: 1px solid
    ${({ $isSelected }) =>
      $isSelected ? "rgba(255, 120, 143, 0.62)" : "rgba(255, 170, 186, 0.4)"};
  background: ${({ $isSelected }) =>
    $isSelected
      ? `radial-gradient(
          130% 100% at 30% 15%,
          rgba(255, 240, 244, 0.98) 0%,
          rgba(255, 220, 228, 0.88) 55%,
          rgba(255, 196, 210, 0.75) 100%
        )`
      : `radial-gradient(
          130% 100% at 30% 15%,
          rgba(255, 250, 252, 0.85) 0%,
          rgba(255, 238, 242, 0.62) 55%,
          rgba(255, 220, 228, 0.48) 100%
        )`};
  box-shadow: ${({ $isSelected }) =>
    $isSelected
      ? `0 6px 16px rgba(211, 71, 99, 0.22),
         inset 0 1px 0 rgba(255, 255, 255, 0.92)`
      : `0 3px 8px rgba(211, 71, 99, 0.1),
         inset 0 1px 0 rgba(255, 255, 255, 0.9)`};

  ${FolderName} {
    color: ${({ $isSelected }) => ($isSelected ? "#c63757" : "#d34763")};
  }

  svg {
    color: ${({ $isSelected }) => ($isSelected ? "#c63757" : "#d34763")};
    path {
      stroke: ${({ $isSelected }) => ($isSelected ? "#c63757" : "#d34763")};
    }
  }

  &:hover {
    border-color: ${({ $isSelected }) =>
      $isSelected ? "rgba(211, 71, 99, 0.75)" : "rgba(255, 120, 143, 0.62)"};
  }

  [data-theme="dark"] & {
    border-color: ${({ $isSelected }) =>
      $isSelected ? "rgba(255, 120, 143, 0.55)" : "rgba(255, 120, 143, 0.28)"};
    background: ${({ $isSelected }) =>
      $isSelected
        ? "rgba(211, 71, 99, 0.32)"
        : "rgba(211, 71, 99, 0.18)"};
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4);

    ${FolderName} {
      color: #ff7896;
    }

    svg {
      color: #ff7896;
      path {
        stroke: #ff7896;
      }
    }

    &:hover {
      background: ${({ $isSelected }) =>
        $isSelected
          ? "rgba(211, 71, 99, 0.42)"
          : "rgba(211, 71, 99, 0.26)"};
      border-color: rgba(255, 120, 143, 0.55);
    }
  }
`;

export const CreateNewItem = styled(FolderItem)`
  border: 1.5px dashed rgba(var(--color-brand-400-rgb), 0.55);
  background: radial-gradient(
    130% 100% at 30% 15%,
    rgba(var(--glass-pale-rgb), 0.82) 0%,
    rgba(var(--glass-pale-rgb), 0.62) 55%,
    rgba(var(--color-brand-200-rgb), 0.48) 100%
  );
  box-shadow:
    0 4px 12px rgba(var(--color-brand-500-rgb), 0.18),
    inset 0 1px 0 rgba(255, 255, 255, 0.9);

  ${FolderName} {
    color: var(--color-brand-600);
    font-weight: 800;
  }

  svg {
    color: var(--color-brand-600);
    path {
      stroke: var(--color-brand-600);
    }
  }

  &:hover {
    border-style: solid;
    border-color: rgba(var(--color-brand-400-rgb), 0.85);
    transform: translateY(-1px);
  }

  [data-theme="dark"] & {
    border-color: rgba(var(--color-brand-400-rgb), 0.4);
    background: rgba(var(--color-brand-700-rgb), 0.22);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4);

    ${FolderName} {
      color: var(--color-brand-300);
    }

    svg {
      color: var(--color-brand-300);
      path {
        stroke: var(--color-brand-300);
      }
    }

    &:hover {
      border-color: rgba(var(--color-brand-400-rgb), 0.7);
      background: rgba(var(--color-brand-700-rgb), 0.32);
    }
  }
`;
